/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import type { ReactNode } from 'react';
import React, { useCallback, useRef, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { getEmptyADF, isEmpty } from '@atlaskit/adf-utils/empty-adf';
import { css, jsx } from '@atlaskit/css';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { token } from '@atlaskit/tokens';

import { useElementBreakpoints, Breakpoint } from '../../hooks/useElementBreakpoints';
import { BackIconButton } from '../BackIconButton/BackIconButton';
import { PromptEditorWrapper } from '../PromptEditorWrapper/PromptEditorWrapper';
import type { PromptEditor } from '../PromptEditorWrapper/PromptEditorWrapper';
import { SubmitButton } from '../SubmitButton/SubmitButton';

import messages from './messages';
import type {
	BlurInputRefType,
	ClearInputMutableRefObject,
	FocusInputMutableRefObject,
} from './useSetInputRef';

const commonTextStyles = css({
	font: token('font.body'),
	wordBreak: 'break-word',
});

const formGapStyles = css({ gap: token('space.050') });

// Make form height equals the height of its parent to prevent the form from growing out of the parent
const formHeightStyles = css({ height: '100%' });

// Prevent form inner from growing out of form
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const formInnerHeightStyles = cssUnbounded({ minHeight: '0px' });

// input's line-height is 20px, logo/button's height is 24px, need to adjust the height
const nextToInputStyles = css({
	marginTop: token('space.negative.025'),
	marginBottom: token('space.negative.025'),
	display: 'flex',
	flexDirection: 'row',
	marginRight: token('space.050'),
	alignItems: 'center',
	alignSelf: 'initial',
});

const selfStartStyles = css({ alignSelf: 'self-start' });

const formContainerStyles = css({
	display: 'flex',
	justifyContent: 'flex-start',
	flex: 1,
	alignItems: 'flex-start',
});

const newPaletteStyles = css({
	borderWidth: token('border.width'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
	paddingTop: token('space.100'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.150'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const formStyles = cssUnbounded({
	// We use '&&' to increase specificity against 'form' element styles in Jira's new issue create modal
	// otherwise the display value is overriden (and the prompt header elements are inadvertently laid out
	// horizontally instead).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		// by default, form elements seem to pick up 12px
		// top margin so we clear it
		margin: 0,
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
	},
});

const formPaddingTopStyles = css({ paddingTop: token('space.025') });

const formInnerStyles = css({ display: 'flex', flex: 1 });

const formInnerPaddingTopStyles = css({ paddingTop: token('space.050') });

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const buttonWrapperStyles = cssUnbounded({
	float: 'right',
	display: 'flex',
	marginLeft: token('space.050'),
});

export type PromptFormProps = {
	adfValue?: JSONDocNode;
	autoFocus?: boolean;
	clearInputRef?: ClearInputMutableRefObject;
	enableLinks?: boolean;
	fireParentActionButton?: () => void;
	focusInputRef?: FocusInputMutableRefObject;
	focusParentActionButton?: (value: boolean) => void;
	isAIFC?: boolean;
	onADFChange?: (inputValue: JSONDocNode) => void;
	onBack?: () => unknown;
	onFormSubmit: (inputValue: string) => void;
	onInputBlur?: (e: React.FocusEvent) => void;
	onInputChange?: (inputValue: string) => void;
	onInputFocus?: () => void;
	onInputKeyDown?: (event: React.KeyboardEvent) => void;
	onInputKeyUp?: (event: React.KeyboardEvent) => void;
	placeholder?: string;
	PromptEditor: PromptEditor;
	refinementTagRef?: React.MutableRefObject<HTMLDivElement | null>;
	showBack?: boolean;
	showButtons?: boolean;
	showLogo?: boolean;
	tag?: ReactNode & { props?: Record<string, unknown> };
	type?: 'user-input' | 'interrogate';
	// When using PromptEditor as input, both value AND adfValue will be set.
	value?: string;
};

export const PromptForm = ({
	type = 'user-input',
	onFormSubmit,
	value = '',
	showButtons = true,
	tag,
	autoFocus = true,
	onInputChange,
	onADFChange,
	onInputFocus,
	onInputBlur,
	placeholder,
	focusInputRef,
	clearInputRef,
	showLogo = true,
	onInputKeyDown,
	adfValue,
	enableLinks,
	PromptEditor,
	showBack,
	onBack,
	onInputKeyUp,
	fireParentActionButton,
	focusParentActionButton,
	refinementTagRef,
	isAIFC,
}: PromptFormProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const [setBreakpointElement, { breakpoint }] = useElementBreakpoints();
	const blurInputRef = useRef<BlurInputRefType>(undefined);
	const [isComposing, setIsComposing] = useState(false);
	const [isLozengeSelected, setIsLozengeSelected] = useState(false);

	const lastAdfValue = useRef<JSONDocNode>(getEmptyADF());

	const isPromptInputEmpty: boolean = Boolean(
		isEmpty(lastAdfValue?.current) && adfValue && isEmpty(adfValue),
	);

	const handleOnCompositionStart = useCallback(() => {
		setIsComposing(true);
	}, []);

	const handleOnCompositionEnd = useCallback(() => {
		setIsComposing(false);
	}, []);

	const handleKeyUp = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (isPromptInputEmpty && focusParentActionButton) {
				focusParentActionButton(true);
			}

			onInputKeyUp && onInputKeyUp(event);
		},
		[focusParentActionButton, onInputKeyUp, isPromptInputEmpty],
	);

	const handleInputFocus = React.useCallback(() => {
		setIsLozengeSelected(false);

		if (onInputFocus) {
			onInputFocus();
		}
	}, [onInputFocus]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			/**
			 * If the key is printable or escape, we should remove the focus ring
			 * that is applied to the parent insert or replace button
			 */
			if ((event.key.length === 1 || event.key === 'Escape') && focusParentActionButton) {
				focusParentActionButton(false);
			}

			if (event.key === 'Backspace' && onBack) {
				// This shortcuts the default functionality by placing focus on the refinement tag
				// instead of removing the tag
				if (!isLozengeSelected && isPromptInputEmpty && refinementTagRef?.current) {
					refinementTagRef.current.getElementsByTagName('button')[0].focus();
					setIsLozengeSelected(true);
					return;
				}

				event.stopPropagation();

				if (isPromptInputEmpty) {
					onBack();
				}
			}

			if (
				event.key === 'Enter' &&
				[event.altKey, event.metaKey, event.ctrlKey].every((isPressed) => !isPressed)
			) {
				event.stopPropagation();
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				const target = event.target as HTMLElement;

				if (isComposing) {
					return;
				}

				// If the prompt input is empty, we should fire the parent action button
				// on Enter, which will either be replace or insert
				if (isPromptInputEmpty && fireParentActionButton) {
					fireParentActionButton();
					return;
				}

				// Handles a case where the user presses enter if refinement tag's close button or the
				// cancel button is focused
				if (
					[
						'close-button-refinement-tag',
						'cancel-icon-button',
						'cancel-esc-button',
						'back-icon-button',
					].includes(target.getAttribute('data-testid') ?? '')
				) {
					return;
				}
				// Handles every other case that is not a "shift + enter"
				if (!event.shiftKey) {
					event.preventDefault();
					onFormSubmit(value);
				}
			}

			/**
			 * Return focus back to the input if the user is typing
			 * and the refinement tag removing button is focused.
			 */
			if (
				!['Enter', 'Backspace', 'Tab'].includes(event.key) &&
				refinementTagRef?.current &&
				refinementTagRef.current.contains(document.activeElement)
			) {
				document.getElementById('ak-editor-textarea')?.focus();
			}
		},
		[
			focusParentActionButton,
			onBack,
			refinementTagRef,
			isLozengeSelected,
			isPromptInputEmpty,
			isComposing,
			fireParentActionButton,
			onFormSubmit,
			value,
		],
	);

	const onADFChange_ = React.useCallback(
		(input: JSONDocNode) => {
			lastAdfValue.current = adfValue ?? getEmptyADF();

			if (onADFChange) {
				onADFChange(input);
			}
		},
		[onADFChange, adfValue],
	);

	const onInputChange_ = React.useCallback(
		(newValue: string) => {
			if (onInputChange) {
				onInputChange(newValue);
			}
		},
		[onInputChange],
	);

	const handleSubmitClick = React.useCallback(() => {
		onFormSubmit(value);
	}, [onFormSubmit, value]);

	return (
		<div
			// ED-19496: We avoid propagating enter keypresses
			// since products like Bitbucket might have parent forms
			// that listen for keydown events to trigger form submission
			role="presentation"
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			css={formContainerStyles}
			ref={setBreakpointElement}
		>
			{showLogo && (
				<div css={[nextToInputStyles, breakpoint <= Breakpoint.Small && selfStartStyles]}>
					<AtlassianIntelligenceIcon
						color={token('color.icon')}
						label={formatMessage(messages.logo)}
						spacing="spacious"
					/>
				</div>
			)}
			{showBack && <BackIconButton inPromptHeader stopPropagation onClick={onBack} />}
			<div
				css={[
					formStyles,
					formGapStyles,
					showLogo && formPaddingTopStyles,
					formHeightStyles,
					type === 'interrogate' && isAIFC && newPaletteStyles,
				]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="ai-modal-prompt-form"
				data-testid="ai-modal-prompt-form"
			>
				{tag}
				{/**
				 * @see packages/editor/editor-core/src/ui/Appearance/FullPage/StyledComponents.ts
				 * The line-height from editorContentArea seems to add some extra pixels (less than 1px)
				 * to the tooltip div around the buttons. Changing the style to match the common text styles
				 * fixes this and prevents height from changing when the submit and cancel buttons appear.
				 */}
				<div
					css={[
						commonTextStyles,
						formInnerStyles,
						!!tag && formInnerPaddingTopStyles,
						formInnerHeightStyles,
					]}
				>
					<PromptEditorWrapper
						PromptEditor={PromptEditor}
						type={type}
						focusInputRef={focusInputRef}
						blurInputRef={blurInputRef}
						clearInputRef={clearInputRef}
						autoFocus={autoFocus}
						defaultValue={adfValue}
						onInputChange={onInputChange_}
						onADFChange={onADFChange_}
						placeholder={placeholder}
						onFocus={handleInputFocus}
						onBlur={onInputBlur}
						onKeyDown={onInputKeyDown}
						onCompositionStart={handleOnCompositionStart}
						onCompositionEnd={handleOnCompositionEnd}
						enableLinks={enableLinks}
					/>
					{showButtons && (
						<div css={[buttonWrapperStyles]}>
							<SubmitButton disabled={!value} onClick={handleSubmitClick} isAIFC={isAIFC} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
